import React, { useState, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  CalendarIcon,
  ShieldExclamationIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const EmployeeDetail = () => {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [paySlips, setPaySlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canViewAllTabs, setCanViewAllTabs] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const { isAuthenticated, user, userRole, locations } = useAuth();

  const addReadAuditLog = useCallback((employee_id) => {
    try {
      // Add an audit log that the current user has viewed this employee record
      const auditLogData = {
        action: 'read',
        employee_id: employee_id,
        changes: 'Read employee record',
        table_name: 'employees',
        performed_by: user
      };

      axios.post(
        `${process.env.REACT_APP_API_URL}/add-audit-log`,
        auditLogData,
        {
          withCredentials: true
        }
      );
    } catch (err) {
      setError('Failed to create audit log');
      console.error('Error creating audit log:', err);
    }
  }, [user]);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        
        // Fetch employee details
        const employeeResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/employees/${id}`,
          {
            withCredentials: true
          }
        );
        setEmployee(employeeResponse.data);
        console.log(employeeResponse.data);

        // Fetch comprehensive timeline
        const timelineResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/employees/${id}/timeline`,
          {
            withCredentials: true
          }
        );
        setTimeline(timelineResponse.data.timeline);

        // Fetch pay slips
        const paySlipsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/employees/${id}/${employeeResponse.data.employee_id}/pay-slips`,
          {
            withCredentials: true
          }
        );
        setPaySlips(paySlipsResponse.data);

        // Check that the location is populated
        let locationName = null;
        if (employeeResponse.data.location !== null) {
          // Add the group name prefix
          locationName = "HRQuery Location Admin " + employeeResponse.data.location.name;
        }

        // Check if the user has permissions to view all tabs if they are a super or location admin
        const doesUserHavePermissions =
          userRole === "super_admin_user_role" ||
          (
            userRole === "location_admin_user_role" &&
            locations?.includes(locationName)
          );
        setCanViewAllTabs(doesUserHavePermissions);

        addReadAuditLog(employeeResponse.data.employee_id);
      } catch (err) {
        setError('Failed to load employee data');
        console.error('Error fetching employee data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (isAuthenticated) {
      fetchEmployeeData();
    }
  }, [id, userRole, locations, isAuthenticated, addReadAuditLog]);

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'on_leave': 'bg-yellow-100 text-yellow-800',
      'terminated': 'bg-red-100 text-red-800',
      'retired': 'bg-purple-100 text-purple-800',
      'suspended': 'bg-orange-100 text-orange-800',
      'probation': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTimelineIcon = (type) => {
    const icons = {
      'career': BuildingOfficeIcon,
      'salary': null, // No specific icon for salary
      'status': null, // No specific icon for status
      'leave': CalendarIcon
    };
    return icons[type] || ShieldExclamationIcon; // Fallback to ShieldExclamationIcon
  };

  const getTimelineColor = (type, color) => {
    const colorMap = {
      'blue': 'bg-blue-500',
      'green': 'bg-green-500',
      'red': 'bg-red-500',
      'yellow': 'bg-yellow-500',
      'purple': 'bg-purple-500',
      'orange': 'bg-orange-500',
      'pink': 'bg-pink-500',
      'gray': 'bg-gray-500'
    };
    return colorMap[color] || 'bg-gray-500';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };



  const getDetailedTimelineAnalysis = () => {
    if (!timeline || timeline.length === 0) return { totalDays: 0, activeDays: 0, leaveDays: 0, gapDays: 0, periods: [], gaps: [] };
    
    // Find the actual first and last dates from timeline instead of using employee.hire_date
    const sortedTimeline = [...timeline].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstDate = new Date(sortedTimeline[0].date);
    
    // Determine if employee is currently active by checking their latest status
    const statusEntries = sortedTimeline.filter(entry => entry.type === 'status');
    const latestStatus = statusEntries.length > 0 ? statusEntries[statusEntries.length - 1] : null;
    const isCurrentlyActive = !latestStatus || latestStatus.details?.status === 'active';
    
    // Use current date if employee is active, otherwise use termination date
    const lastDate = isCurrentlyActive ? new Date() : new Date(employee.termination_date);
    
    // Debug logging
    console.log('Employee hire_date:', employee.hire_date);
    console.log('First timeline date:', sortedTimeline[0].date);
    console.log('Latest status:', latestStatus);
    console.log('Is currently active:', isCurrentlyActive);
    console.log('Last date used:', lastDate);
    console.log('Employee termination_date:', employee.termination_date);
    console.log('Full timeline:', sortedTimeline);
    
    const totalDays = Math.floor((lastDate - firstDate) / (1000 * 60 * 60 * 24));
    console.log('Calculated totalDays:', totalDays);
    
    let leaveDays = 0;
    let employmentGapDays = 0;
    
    // Calculate leave days from actual leave records
    timeline.forEach(entry => {
      if (entry.type === 'leave' && entry.details?.days_approved) {
        const days = parseInt(entry.details.days_approved) || 0;
        leaveDays += days;
        console.log('Found leave:', entry.title, days, 'days');
      }
    });
    
    // Calculate employment gaps (periods of termination/unemployment)
    let inGap = false;
    let gapStart = null;
    const gaps = [];
    
    sortedTimeline.forEach((entry, index) => {
      if (entry.type === 'status') {
        if (entry.details?.status === 'terminated' && !inGap) {
          // Start of gap
          inGap = true;
          gapStart = new Date(entry.date);
          console.log('Gap started:', entry.date);
        } else if (entry.details?.status === 'active' && inGap && gapStart) {
          // End of gap
          const gapEnd = new Date(entry.date);
          const gapDuration = Math.floor((gapEnd - gapStart) / (1000 * 60 * 60 * 24));
          employmentGapDays += gapDuration;
          
          // Add to gaps array for display
          gaps.push({
            start: gapStart.toISOString().split('T')[0],
            end: gapEnd.toISOString().split('T')[0],
            days: gapDuration,
            reason: 'Employment gap (terminated)',
            previousStatus: 'terminated',
            nextStatus: 'active'
          });
          
          console.log('Gap ended:', entry.date, '- Duration:', gapDuration, 'days');
          inGap = false;
          gapStart = null;
        }
      }
    });
    
    // Active days = total timeline period minus leave days minus unemployment gaps
    const activeDays = Math.max(0, totalDays - leaveDays - employmentGapDays);
    
    console.log('Final calculation - Total:', totalDays, 'Leave:', leaveDays, 'Gaps:', employmentGapDays, 'Active:', activeDays);
    
    return { 
      totalDays: Math.max(0, totalDays), 
      activeDays: Math.max(0, activeDays), 
      leaveDays: Math.max(0, leaveDays), 
      gapDays: Math.max(0, employmentGapDays), 
      periods: [], 
      gaps: gaps // We'll calculate this separately if needed
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ShieldExclamationIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-medium text-gray-900">Employee not found</h2>
        <p className="mt-1 text-sm text-gray-500">The employee you're looking for doesn't exist.</p>
      </div>
    );
  }

  const timelineAnalysis = getDetailedTimelineAnalysis();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-6">
        <div>
          <h1 className="text-3xl font-light text-gray-900">{employee.name}</h1>
          <p className="mt-2 text-sm text-gray-600">
            Employee ID: {employee.employee_id}
          </p>
        </div>
        <Link
          to="/employees"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Back to Employees
        </Link>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>

          {canViewAllTabs && (
            <>
              <button
                onClick={() => setActiveTab('timeline')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'timeline'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Timeline
              </button>

              <button
                onClick={() => setActiveTab('investigation')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'investigation'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ShieldExclamationIcon className="h-4 w-4 inline mr-1" />
                Timeline Analysis
              </button>

              <button
                onClick={() => setActiveTab('assignments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'assignments'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Work Assignments
              </button>

              <button
                onClick={() => setActiveTab('pay-slips')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pay-slips'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pay Slips
              </button>

              <button
                onClick={() => setActiveTab('confidential')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'confidential'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🔒 Confidential
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Employee Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Employee Information</h3>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{employee.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900">{employee.email || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Employee ID</dt>
                <dd className="mt-1 text-sm text-gray-900">{employee.employee_id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Hire Date</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(employee.hire_date)}</dd>
              </div>
              {employee.termination_date && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Termination Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(employee.termination_date)}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(employee.is_active ? 'active' : 'inactive')}`}>
                    {employee.is_active ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Position & Location */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Position & Location</h3>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Position</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {employee.position ? (
                    <div>
                      <div className="font-medium">{employee.position.title}</div>
                      <div className="text-gray-500">{employee.position.department}</div>
                      {employee.position.level && (
                        <div className="text-gray-500">Level: {employee.position.level}</div>
                      )}
                    </div>
                  ) : (
                    'Not assigned'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Location</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {employee.location ? (
                    <div>
                      <div className="font-medium">{employee.location.name}</div>
                      {employee.location.city && employee.location.state && (
                        <div className="text-gray-500">
                          {employee.location.city}, {employee.location.state}
                        </div>
                      )}
                    </div>
                  ) : (
                    'Not assigned'
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {activeTab === 'timeline' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Employment Timeline</h3>
            <p className="mt-1 text-sm text-gray-500">
              Complete history of career progression, status changes, and leave records
            </p>
          </div>
          
          <div className="p-6">
            {timeline.length === 0 ? (
              <div className="text-center py-8">
                <ShieldExclamationIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No timeline data</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Upload CSV data to populate the employee timeline.
                </p>
              </div>
            ) : (
              <div className="flow-root">
                <ul className="-mb-8">
                  {timeline.map((entry, entryIdx) => {
                    const Icon = getTimelineIcon(entry.type);
                    return (
                      <li key={entryIdx}>
                        <div className="relative pb-8">
                          {entryIdx !== timeline.length - 1 ? (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          ) : null}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${getTimelineColor(entry.type, entry.color)}`}>
                                <Icon className="h-5 w-5 text-white" aria-hidden="true" />
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-500">
                                  <span className="font-medium text-gray-900">{entry.title}</span>
                                </p>
                                <p className="text-sm text-gray-500">{entry.description}</p>
                                
                                {/* Additional Details */}
                                {entry.details && Object.keys(entry.details).length > 0 && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    {entry.type === 'career' && entry.details.position && (
                                      <div>Position: {entry.details.position}</div>
                                    )}
                                    {entry.type === 'career' && entry.details.location && (
                                      <div>Location: {entry.details.location}</div>
                                    )}
                                    {entry.type === 'salary' && entry.details.salary && (
                                      <div>Salary: ${entry.details.salary.toLocaleString()} {entry.details.currency}</div>
                                    )}
                                    {entry.type === 'status' && entry.details.status && (
                                      <div>Status: {entry.details.status}</div>
                                    )}
                                    {entry.type === 'leave' && entry.details.leave_type && (
                                      <div>Leave Type: {entry.details.leave_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                                    )}
                                    {entry.details.reason && (
                                      <div>Reason: {entry.details.reason}</div>
                                    )}
                                    {entry.type === 'leave' && entry.details.workers_comp_case_number && (
                                      <div className="text-red-600 font-medium">
                                        Workers Comp Case: {entry.details.workers_comp_case_number}
                                      </div>
                                    )}
                                    {entry.type === 'leave' && entry.details.disability_claim_number && (
                                      <div className="text-orange-600 font-medium">
                                        Disability Claim: {entry.details.disability_claim_number}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                <time dateTime={entry.date}>{formatDate(entry.date)}</time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'investigation' && (
        <div className="space-y-8">
          {/* Timeline Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="mb-8">
              <h3 className="text-xl font-light text-gray-900 mb-2">Employment Timeline Analysis</h3>
              <p className="text-sm text-gray-600">
                Full Employment History: {timeline && timeline.length > 0 ? formatDate([...timeline].sort((a, b) => new Date(a.date) - new Date(b.date))[0].date) : 'N/A'} to {(() => {
                  if (!timeline || timeline.length === 0) return 'N/A';
                  const statusEntries = timeline.filter(entry => entry.type === 'status');
                  const latestStatus = statusEntries.length > 0 ? statusEntries[statusEntries.length - 1] : null;
                  const isCurrentlyActive = !latestStatus || latestStatus.details?.status === 'active';
                  return isCurrentlyActive ? 'Present' : (employee.termination_date ? formatDate(employee.termination_date) : 'Present');
                })()}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center border-r border-gray-100 last:border-r-0">
                <div className="text-3xl font-light text-gray-900 mb-2">{timelineAnalysis.activeDays.toLocaleString()}</div>
                <p className="text-sm font-medium text-gray-700 mb-1">Active Days</p>
                <p className="text-xs text-gray-500">
                  {timelineAnalysis.totalDays > 0 ? Math.round((timelineAnalysis.activeDays / timelineAnalysis.totalDays) * 100) : 0}%
                </p>
              </div>
              
              <div className="text-center border-r border-gray-100 last:border-r-0">
                <div className="text-3xl font-light text-gray-900 mb-2">{timelineAnalysis.leaveDays.toLocaleString()}</div>
                <p className="text-sm font-medium text-gray-700 mb-1">Leave Days</p>
                <p className="text-xs text-gray-500">
                  {timelineAnalysis.totalDays > 0 ? Math.round((timelineAnalysis.leaveDays / timelineAnalysis.totalDays) * 100) : 0}%
                </p>
              </div>
              
              <div className="text-center border-r border-gray-100 last:border-r-0">
                <div className="text-3xl font-light text-gray-900 mb-2">{timelineAnalysis.gapDays.toLocaleString()}</div>
                <p className="text-sm font-medium text-gray-700 mb-1">Gap Days</p>
                <p className="text-xs text-gray-500">
                  {timelineAnalysis.totalDays > 0 ? Math.round((timelineAnalysis.gapDays / timelineAnalysis.totalDays) * 100) : 0}%
                </p>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-light text-gray-900 mb-2">{timelineAnalysis.totalDays.toLocaleString()}</div>
                <p className="text-sm font-medium text-gray-700 mb-1">Total Days</p>
                <p className="text-xs text-gray-500">
                  {Math.round(timelineAnalysis.totalDays / 365 * 10) / 10} years
                </p>
              </div>
            </div>
          </div>

          {/* Timeline Events */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h3 className="text-xl font-light text-gray-900 mb-6">Timeline Events</h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">
                      Event
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {timeline && timeline.map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {formatDate(entry.date)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full border bg-gray-50 text-gray-700 border-gray-200">
                          {entry.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{entry.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{entry.description}</div>
                        {entry.details?.days_approved && (
                          <div className="text-xs text-gray-500 mt-1">
                            Duration: {entry.details.days_approved} days
                          </div>
                        )}
                        {entry.details?.workers_comp_case_number && (
                          <div className="text-xs text-gray-500 mt-1">
                            Workers' Comp: {entry.details.workers_comp_case_number}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Employment Gaps Analysis */}
          {timelineAnalysis.gaps && timelineAnalysis.gaps.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-xl font-light text-gray-900 mb-6">
                Employment Gaps ({timelineAnalysis.gaps.length})
              </h3>
              
              <div className="space-y-6">
                {timelineAnalysis.gaps.map((gap, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-medium text-gray-900 mb-3">
                          Gap #{index + 1}: {gap.days} days
                        </p>
                        <div className="space-y-2 text-sm text-gray-600">
                          <p><span className="font-medium">From:</span> {formatDate(gap.start)}</p>
                          <p><span className="font-medium">To:</span> {formatDate(gap.end)}</p>
                          <p><span className="font-medium">Reason:</span> {gap.reason}</p>
                        </div>
                      </div>
                      <div>
                        <div className="space-y-3 text-sm text-gray-600">
                          <p>
                            <span className="font-medium">Previous Status:</span>
                            <span className="ml-2 inline-flex px-3 py-1 text-xs font-medium rounded-full border bg-gray-50 text-gray-700 border-gray-200">
                              {gap.previousStatus}
                            </span>
                          </p>
                          <p>
                            <span className="font-medium">Next Status:</span>
                            <span className="ml-2 inline-flex px-3 py-1 text-xs font-medium rounded-full border bg-gray-50 text-gray-700 border-gray-200">
                              {gap.nextStatus}
                            </span>
                          </p>
                          <div>
                            <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full border bg-gray-50 text-gray-700 border-gray-200">
                              {gap.days > 90 ? 'Extended Gap' : gap.days > 30 ? 'Medium Gap' : 'Short Gap'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Investigation Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h3 className="text-xl font-light text-gray-900 mb-6">Investigation Summary</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Employee:</span> {employee.name}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Employee ID:</span> {employee.employee_id}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Analysis Date:</span> {new Date().toLocaleDateString()}
                  </p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-6">
                  <h4 className="font-medium text-gray-900 mb-4">Timeline Metrics</h4>
                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Total Employment Period:</span>
                      <span className="font-medium">{timelineAnalysis.totalDays.toLocaleString()} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Active Work Days:</span>
                      <span className="font-medium">{timelineAnalysis.activeDays.toLocaleString()} days ({timelineAnalysis.totalDays > 0 ? Math.round((timelineAnalysis.activeDays / timelineAnalysis.totalDays) * 100) : 0}%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Time on Leave:</span>
                      <span className="font-medium">{timelineAnalysis.leaveDays.toLocaleString()} days ({timelineAnalysis.totalDays > 0 ? Math.round((timelineAnalysis.leaveDays / timelineAnalysis.totalDays) * 100) : 0}%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Employment Gaps:</span>
                      <span className="font-medium">{timelineAnalysis.gapDays.toLocaleString()} days ({timelineAnalysis.totalDays > 0 ? Math.round((timelineAnalysis.gapDays / timelineAnalysis.totalDays) * 100) : 0}%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status Changes:</span>
                      <span className="font-medium">{timeline.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Gap Periods:</span>
                      <span className="font-medium">{timelineAnalysis.gaps.length}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Key Observations</h4>
                <div className="space-y-4 text-sm text-gray-600">
                  {timelineAnalysis.gapDays > 90 && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-gray-900 mb-1">Extended Employment Gaps</p>
                      <p>Total gap time exceeds 90 days</p>
                    </div>
                  )}
                  
                  {timelineAnalysis.leaveDays > timelineAnalysis.activeDays && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-gray-900 mb-1">High Leave Ratio</p>
                      <p>Leave time exceeds active work time</p>
                    </div>
                  )}
                  
                  {timelineAnalysis.gaps.length > 3 && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-gray-900 mb-1">Multiple Employment Gaps</p>
                      <p>{timelineAnalysis.gaps.length} separate gap periods identified</p>
                    </div>
                  )}
                  
                  {timelineAnalysis.gapDays === 0 && timelineAnalysis.gaps.length === 0 && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-gray-900 mb-1">Continuous Employment</p>
                      <p>No significant employment gaps detected</p>
                    </div>
                  )}
                  
                  {timelineAnalysis.gapDays <= 90 && timelineAnalysis.leaveDays <= timelineAnalysis.activeDays && timelineAnalysis.gaps.length <= 3 && timelineAnalysis.gaps.length > 0 && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-gray-900 mb-1">Standard Employment Pattern</p>
                      <p>Employment history shows typical patterns</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Work Assignments Tab */}
      {activeTab === 'assignments' && (
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Work Assignments</h3>
          <p className="text-gray-600 mb-6">Complete history of job assignments, locations, and position changes</p>
          
          {/* Work Assignments Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Effective From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Effective To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timeline
                  .filter(event => event.type === 'career')
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((event, index) => {
                    const nextEvent = timeline
                      .filter(e => e.type === 'career')
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .find(e => new Date(e.date) > new Date(event.date));
                    
                    const endDate = nextEvent ? 
                      new Date(nextEvent.date).toLocaleDateString() : 
                      'Current';
                    
                                         // Parse assignment details from event (with null checks)
                     const details = event.details || event.event || '';
                     const assignmentMatch = typeof details === 'string' ? details.match(/Started as (.+?) at (.+)/) : null;
                     const position = assignmentMatch ? assignmentMatch[1] : (event.event || '').replace('Career Change: ', '');
                     const location = assignmentMatch ? assignmentMatch[2] : 'N/A';
                     const assignment = `${location} - ${position}`;
                    
                    return (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {assignment}
                          </div>
                          <div className="text-sm text-gray-500">
                            {position}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(event.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {endDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {event.type === 'career' ? 'Assignment Change' : 'Position Update'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          
          {timeline.filter(event => event.type === 'career').length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No work assignment history available
            </div>
          )}
        </div>
      )}

      {/* Pay Slips Tab */}
      {activeTab === 'pay-slips' && (
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Pay Slips</h3>
          <p className="text-gray-600 mb-6">View pay slips for this employee</p>
          
          {/* Pay Slips Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pay Slip
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paySlips.map((paySlip, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">{paySlip.file_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <a
                        href={paySlip.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        View file
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {paySlips.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No pay slips available
            </div>
          )}
        </div>
      )}

      {/* Confidential Information Tab */}
      {activeTab === 'confidential' && (
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">🔒 Confidential Information</h3>
          <div className="text-gray-600">
            <p>Password-protected sensitive employee information will be displayed here.</p>
            <p className="text-sm mt-2 text-gray-500">Security verification required...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDetail; 